import { PlusOutlined } from "@ant-design/icons";
import { faClockRotateLeft, faEye, faPencil, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Pagination, Switch, Table, Tooltip } from "antd";
import { ProhibitedArea } from "components";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import { useEffect, useState } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Column } = Table;

const BusinessAreaList = ({
    permission,
    businessAreaList,
    onAddBusinessArea,
    onViewBusinessArea,
    onEditBusinessArea,
    onDeleteBusinessArea,
    onShowBusinessAreaHistory,
    onActivateDeactivateBusinessArea,
    onPageSelect,
}) => {
    const [pagination, setPagination] = useState({ defaultPageSize: 100, pageSize: 100, page: 1 });
    const [paginatedItems, setPaginatedItems] = useState(businessAreaList && businessAreaList.slice(pagination.page - 1, pagination.pageSize));

    useEffect(() => {
        setPaginatedItems(businessAreaList && businessAreaList.slice((pagination.page - 1) * pagination.pageSize, pagination.page * pagination.pageSize));
    }, [businessAreaList, pagination]);

    const onPageClick = (page, pageSize) => {
        let currentPagination = { ...pagination };
        let currentPaginatedItems = businessAreaList && businessAreaList.slice((page - 1) * pageSize, page * pageSize);
        setPaginatedItems(currentPaginatedItems);
        currentPagination.page = page;
        currentPagination.pageSize = pageSize;
        setPagination(currentPagination);
    };

    return !permission ? (
        <ProhibitedArea></ProhibitedArea>
    ) : (
        <FlexContainerLayout
            footerClassName="justify-between"
            footer={
                <>
                    <Button disabled={!permission.canAdd} type="primary" onClick={onAddBusinessArea} data-testid={CypressTestIds.ADD_BUSINESS_AREA_BUTTON}>
                        <PlusOutlined /> Add Business Area
                    </Button>
                    <Pagination
                        size="small"
                        hideOnSinglePage={true}
                        total={businessAreaList.length}
                        defaultPageSize={pagination.defaultPageSize}
                        pageSize={pagination.pageSize}
                        responsive={true}
                        showSizeChanger={false}
                        onChange={onPageClick}
                    />
                </>
            }
        >
            <Table
                className="flex size-full flex-1 flex-col [&_thead]:sticky [&_thead]:top-0 [&_thead]:z-10 [&_thead]:bg-header-footer [&_thead]:text-header-footer"
                size="small"
                rowKey="businessAreaId"
                dataSource={paginatedItems}
                pagination={false}
            >
                <Column
                    width="80px"
                    render={(value, businessArea, index) => (
                        <div className="flex gap-2">
                            <Tooltip title="Edit Business Area">
                                <FontAwesomeIcon
                                    icon={faPencil}
                                    className={businessArea.type !== "SYSTEM" && permission.canEdit ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                    data-testid={CypressTestIds.EDIT_BUSINESS_AREA_BUTTON}
                                    onClick={() => (businessArea.type !== "SYSTEM" && permission.canEdit ? onEditBusinessArea(businessArea.businessAreaId) : null)}
                                />
                            </Tooltip>
                            <Tooltip title="View Business Area">
                                <FontAwesomeIcon
                                    icon={faEye}
                                    className={permission.canView ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                    data-testid={CypressTestIds.VIEW_BUSINESS_AREA_BUTTON}
                                    onClick={() => (permission.canView ? onViewBusinessArea(businessArea.businessAreaId) : null)}
                                />
                            </Tooltip>
                            <Tooltip title="View Business Area History">
                                <FontAwesomeIcon
                                    icon={faClockRotateLeft}
                                    className={permission.canView ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                    data-testid={CypressTestIds.HISTORY_BUSINESS_AREA_BUTTON}
                                    onClick={() => (permission.canView ? onShowBusinessAreaHistory(businessArea.businessAreaId) : null)}
                                />
                            </Tooltip>
                        </div>
                    )}
                />
                <Column
                    width="15%"
                    dataIndex={"name"}
                    title={"Business Area"}
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, businessArea, index) => (
                        <Tooltip placement="topLeft" title={value}>
                            {value}
                        </Tooltip>
                    )}
                />
                <Column
                    width="10%"
                    dataIndex={"description"}
                    title={"Description"}
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, businessArea, index) => (
                        <Tooltip placement="topLeft" title={value}>
                            {value}
                        </Tooltip>
                    )}
                />
                <Column
                    width="8%"
                    dataIndex={"usersCount"}
                    title={"Users"}
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, businessArea, index) => <>{value || 0}</>}
                />
                <Column
                    width="8%"
                    dataIndex={"apiKeysCount"}
                    title={"API Keys"}
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, businessArea, index) => <>{value || 0}</>}
                />
                <Column
                    width="8%"
                    dataIndex={"systemConnectionsCount"}
                    title={"System Connections"}
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, businessArea, index) => <>{value || 0}</>}
                />
                <Column
                    width="8%"
                    dataIndex={"dataDomainCount"}
                    title={"Domains"}
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, businessArea, index) => <>{value || 0}</>}
                />
                <Column
                    width="8%"
                    dataIndex={"schemasCount"}
                    title={"Schemas"}
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, businessArea, index) => <>{value || 0}</>}
                />
                <Column
                    width="8%"
                    dataIndex={"dataSetCount"}
                    title={"DataSets"}
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, businessArea, index) => <>{value || 0}</>}
                />
                <Column
                    width="10%"
                    dataIndex="active"
                    title={"Active"}
                    ellipsis={{
                        showTitle: false,
                    }}
                    render={(value, businessArea, index) => {
                        return (
                            <Switch
                                checked={value}
                                disabled={businessArea.type === "SYSTEM" || !permission.canEdit}
                                data-testid={CypressTestIds.ACTIVATE_BUSINESS_AREA_SWITCH}
                                onClick={(checked, event) => onActivateDeactivateBusinessArea(businessArea.businessAreaId, businessArea.version, checked)}
                            ></Switch>
                        );
                    }}
                />
                <Column
                    width="5%"
                    render={(value, businessArea, index) => {
                        return (
                            <Tooltip title="Delete Business Area">
                                <FontAwesomeIcon
                                    icon={faTrashCan}
                                    className={businessArea.type !== "SYSTEM" && permission.canDelete ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                    data-testid={CypressTestIds.DELETE_BUSINESS_AREA_BUTTON}
                                    onClick={() => (businessArea.type !== "SYSTEM" && permission.canDelete ? onDeleteBusinessArea(businessArea) : null)}
                                />{" "}
                            </Tooltip>
                        );
                    }}
                />
            </Table>
        </FlexContainerLayout>
    );
};

export default BusinessAreaList;
